import { useCallback, useState } from 'react';
import { last } from 'lodash';
import { isString } from 'lodash';
import {
  MomentApplication,
  ProductListProduct,
  ProductListProductVariant,
} from './moments.types';
import { getStorageValue } from '../../../utils/storage';
import { DateTime } from 'luxon';
import {
  isOptionSelected,
  transformVariantToMultiSelectOption,
} from '../updateVariant';
import { multiSelectType } from '../../components/MultiSelectDropdown';

export const mockMoments: MomentApplication[] = Array.from(
  { length: 20 },
  (_, index) => ({
    id: String(index + 1),
    isEarned: index % 2 === 0,
    isClaimed: false,
    canBeClaimed: index % 2 === 0,
    earnedDates: {},
    unearnedDates: {},
    claimedDates: {},
    moment: {
      id: String(index + 1),
      position: index + 1,
      displayName: `Moment ${index + 1}`,
      description: `Description of Moment ${index + 1}`,
      isUnearnable: index % 2 === 0,
      isReearnable: index % 2 === 0,
      canBeClaimed: index % 2 === 0,
      actions: [{}],
      isVisible: index % 2 === 0,
      badgeOnSrc: `https://robohash.org/${String(index + 1)}`,
      badgeOffSrc: `https://robohash.org/${String(index + 1)}`,
      claimBannerMetadata: {
        title: `Claim Banner Title ${index + 1}`,
        description: `Claim Banner Description ${index + 1}`,
        claimBtnText: `Claim Banner CTA Text ${index + 1}`,
        graphicSrc: `https://www.google.com/search?q=${index + 1}`,
        isEnabled: index % 2 === 0,
      },
    },
  }),
);

export const momentCanBeClaimed = (moment: MomentApplication): boolean =>
  moment?.isEarned &&
  !moment?.isClaimed &&
  moment?.canBeClaimed &&
  !!moment?.moment?.claimBannerMetadata;

export const momentIsDismissed = (
  subscriptionId: string,
  momentApp: MomentApplication,
): boolean => {
  const momentId = momentApp.moment.id;
  const storageKey = getMomentStorageKey(subscriptionId, momentId);
  const storageValue = getStorageValue(storageKey);

  let isDismissed = false;
  if (storageValue === true) {
    isDismissed = true;
  } else if (isString(storageValue) && Array.isArray(momentApp.earnedDates)) {
    const dismissedAt = DateTime.fromISO(storageValue);
    const earnedAt = DateTime.fromISO(last(momentApp.earnedDates));
    isDismissed = earnedAt < dismissedAt;
  }

  return isDismissed;
};

export const useBannerAnimation = (): {
  className: string;
  onAnimationEnd: () => void;
  animate: (className: string) => void;
} => {
  const [className, setClassName] = useState('');

  const onAnimationEnd = useCallback(() => {
    setClassName('');
  }, []);

  const animate = useCallback((className: string) => {
    setClassName(className);
  }, []);

  return { className, onAnimationEnd, animate };
};

export const getMomentStorageKey = (subscriptionId: string, momentId: string) =>
  `sub-${subscriptionId}-moment-${momentId}`;

export const productListProductToGlobalProduct = (
  productListProduct: ProductListProduct,
): GlobalProduct => {
  const {
    id,
    title,
    descriptionHtml,
    description,
    featuredImage,
    variants,
    handle,
    priceRangeV2,
  } = productListProduct;

  const globalProduct: GlobalProduct = {
    id,
    title,
    descriptionHtml,
    description,
    handle,
    price: '',
    priceMin: priceRangeV2.minVariantPrice.amount,
    priceMax: priceRangeV2.maxVariantPrice.amount,
    discountedPriceMin: +(
      productListProduct?.discountedPriceMin ||
      priceRangeV2.minVariantPrice.amount
    ),
    discountedPriceMax: +(
      productListProduct.discountedPriceMax ||
      priceRangeV2.maxVariantPrice.amount
    ),
    priceCurrency: priceRangeV2.maxVariantPrice.currencyCode,
    productId: id,
    image: featuredImage?.transformedSrc,
    tags: [],
    variants: variants.map((variant) =>
      productListProductVariantToGlobalVariant({
        productListProductVariant: variant,
        productListProduct,
      }),
    ),
  };
  const variantsOptions = globalProduct.variants?.map((v) => {
    const isSelected = v.id === globalProduct.variants[0].id;
    return transformVariantToMultiSelectOption(v, isSelected);
  });
  const defaultVariant = globalProduct.variants[0];

  const optionsAndValues = productListProduct?.options
    ?.map(({ name, values, id }) => {
      const variantOptions = values?.map((label) => ({
        label,
        id: label,
        type: multiSelectType.OPTION,
        optionName: name,
        isSelected: isOptionSelected(defaultVariant, {
          name,
          value: label,
        }),
      }));
      return [
        { label: name, id, type: multiSelectType.TITLE },
        ...variantOptions,
      ];
    })
    .flat();
  return { ...globalProduct, variantsOptions, optionsAndValues };
};

const productListProductVariantToGlobalVariant = ({
  productListProductVariant,
  productListProduct,
}: {
  productListProductVariant: ProductListProductVariant;
  productListProduct: ProductListProduct;
}): GlobalVariant => {
  const {
    id,
    displayName,
    price,
    title,
    image,
    selectedOptions,
    compareAtPrice,
  } = productListProductVariant;
  const globalVariant: GlobalVariant = {
    ...productListProductVariant,
    id,
    title: displayName,
    price: compareAtPrice || price,
    available: true,
    image: image?.transformedSrc,
    name: title,
    productName: productListProduct.title,
    productId: productListProduct.id,
    description: productListProduct.description,
    descriptionHtml: productListProduct.descriptionHtml,
    options: productListProduct.options?.map(({ name }) => name),
    images: productListProduct.images?.map(({ url }) => url),
    selectedOptions,
    discountedPrice: price,
  };
  return globalVariant;
};
